import mapboxgl from 'mapbox-gl'
import { EFlightAreaType } from '../types/flight-area'

import { getRoot } from '/@/root'
import rootStore from '/@/store'
import { GeojsonCoordinate } from '/@/types/map'
import router from '/@/router'
import { setCurtain, setCurtainColor, removeCurtain } from './use-curtain'
import { setVolume, setVolumeColor, removeVolume } from './use-volume'

// Mode d'élévation d'une annotation :
//  - 'agl' : `value` = hauteur au-dessus du sol, l'élément suit le relief (drape).
//  - 'msl' : `value` = altitude absolue (ASL), l'élément est plat à cette altitude.
// `curtain` : afficher la jupe verticale reliant l'élément au sol.
export interface ElevationOpts {
  mode?: 'agl' | 'msl'
  fixedAltitude?: number
  curtain?: boolean
  // Volume polygone (maillage conforme, use-volume.ts) : plancher/plafond, chacun
  // 'agl' (suit le sol) ou 'msl' (altitude absolue). Volume actif quand ceiling > floor.
  floor?: number
  floorRef?: 'agl' | 'msl'
  ceiling?: number
  ceilingRef?: 'agl' | 'msl'
}

// Densifie un tracé (un sommet ~tous les 15 m). Une ligne Mapbox élevée
// (line-elevation-reference: ground) n'échantillonne le sol QU'À ses sommets et
// trace une corde droite entre eux : à 2 sommets elle ne suit pas le relief. En
// ajoutant des sommets intermédiaires, le sol est échantillonné le long du tracé
// et la ligne drape sur la surface (c'est pourquoi les traces de vol, très denses,
// drapent déjà). On ne densifie que l'affichage, pas la géométrie persistée.
function densifyPath (coords: GeojsonCoordinate[], stepM = 15): GeojsonCoordinate[] {
  if (!coords || coords.length < 2) return coords
  const R = 6371000
  const distM = (a: GeojsonCoordinate, b: GeojsonCoordinate) => {
    const dLat = (b[1] - a[1]) * Math.PI / 180
    const dLng = (b[0] - a[0]) * Math.PI / 180
    const x = Math.sin(dLat / 2) ** 2 +
      Math.cos(a[1] * Math.PI / 180) * Math.cos(b[1] * Math.PI / 180) * Math.sin(dLng / 2) ** 2
    return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x))
  }
  const out: GeojsonCoordinate[] = []
  for (let i = 0; i < coords.length - 1; i++) {
    const a = coords[i]
    const b = coords[i + 1]
    const n = Math.max(1, Math.min(2000, Math.round(Math.max(distM(a, b), 0.001) / stepM)))
    for (let k = 0; k < n; k++) {
      const t = k / n
      out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t] as GeojsonCoordinate)
    }
  }
  out.push(coords[coords.length - 1])
  return out
}

function circleToPolygonCoords (center: [number, number], radiusMeters: number, steps = 64): [number, number][] {
  const [lng, lat] = center
  const earthRadius = 6371000
  const coords: [number, number][] = []
  for (let i = 0; i <= steps; i++) {
    const angle = (i * 2 * Math.PI) / steps
    const dlat = (radiusMeters * Math.cos(angle)) / earthRadius * (180 / Math.PI)
    const dlng = (radiusMeters * Math.sin(angle)) / (earthRadius * Math.cos(lat * Math.PI / 180)) * (180 / Math.PI)
    coords.push([lng + dlng, lat + dlat])
  }
  return coords
}

export function useGMapCover () {
  const root = getRoot()
  const store = rootStore
  const coverMap = store.state.coverMap

  const normalColor = '#2D8CF0'
  const flightAreaColorMap = {
    [EFlightAreaType.DFENCE]: '#19be6b',
    [EFlightAreaType.NFZ]: '#ff0000',
  }
  const disableColor = '#b3b3b3'

  function getPinIconUrl (color?: string): string {
    const c = color || '#2D8CF0'
    // Replicate the original pin-*.svg shape: hollow diamond (outer ring) with even-odd fill rule.
    // Path identical to the original static SVG files, colour injected dynamically.
    const svg = `<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <g transform="translate(5,2)" fill="${c}" fill-rule="evenodd">
        <path d="M6.56239716,0 L13.1247943,9.89949494 L6.56239716,19.7989899 L0,9.89949494 Z M6.56239716,3.61897251 L2.39965953,9.89878783 L6.56239716,16.1786032 L10.7251348,9.89878783 Z"/>
      </g>
    </svg>`
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
  }

  function init2DPin (name: string, coordinates: GeojsonCoordinate, color?: string, data?: { id: string, [key: string]: any }, height = 0, opts: ElevationOpts = {}) {
    const map: mapboxgl.Map = root.$map
    if (!map) return

    const isMsl = opts.mode === 'msl'
    const fixedAlt = Number(opts.fixedAltitude) || 0
    const terrainAt = (lng: number, lat: number) => ((map as any).queryTerrainElevation({ lng, lat }, { exaggerated: true }) ?? 0)
    // Marker.setAltitude est RELATIF au terrain (z final = terrain + altitude), pas absolu.
    //  - 'agl' : altitude = hauteur au-dessus du sol.
    //  - 'msl' : altitude = A − terrain → z final = terrain + (A − terrain) = A (absolu).
    const elevated = isMsl || height > 0
    const altAt = (lng: number, lat: number) => isMsl ? (fixedAlt - terrainAt(lng, lat)) : height

    const el = document.createElement('img')
    el.src = getPinIconUrl(color)
    el.style.width = '30px'
    el.style.height = '40px'
    el.style.cursor = 'pointer'

    const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom', draggable: false })
      .setLngLat([coordinates[0], coordinates[1]])
    if (elevated) (marker as any).setAltitude?.(altAt(coordinates[0], coordinates[1]))
    marker.addTo(map)

    const curtainOpts = (c?: string) => ({ mode: (isMsl ? 'msl' : 'agl') as 'agl' | 'msl', value: isMsl ? fixedAlt : height, color: c || color })

    if (data?.id) {
      const id = data.id
      if (opts.curtain && elevated) setCurtain(id, [[coordinates[0], coordinates[1]]], curtainOpts())

      el.addEventListener('click', () => {
        store.commit('SET_SELECTED_MAP_ELEMENT', id)
        router.push({ name: 'layer' })
      })

      coverMap[id] = [{
        type: 'marker',
        marker,
        remove: () => { removeCurtain(id); marker.remove() },
        setLngLat: (lnglat: [number, number]) => {
          marker.setLngLat(lnglat)
          if (elevated) (marker as any).setAltitude?.(altAt(lnglat[0], lnglat[1]))
          if (opts.curtain && elevated) setCurtain(id, [[lnglat[0], lnglat[1]]], curtainOpts())
        },
        setIcon: (iconUrl: string) => { el.src = iconUrl },
        setDraggable: (b: boolean) => { marker.setDraggable(b); el.style.cursor = b ? 'move' : 'pointer' },
        getExtData: () => data,
      }]
    }
  }

  function initPolyline (name: string, coordinates: GeojsonCoordinate[], color?: string, data?: { id: string, [key: string]: any }, height = 0, perVertexElevation = false, opts: ElevationOpts = {}) {
    const map: mapboxgl.Map = root.$map
    if (!map) return
    const id = data?.id
    if (!id) return

    const sourceId = `polyline-${id}`
    const layerId = `polyline-layer-${id}`
    let currentColor = color || normalColor

    // Trace de vol : la hauteur AGL est portée par l'altitude de chaque coordonnée
    // (3e composante), variable d'un point à l'autre. Sinon, hauteur constante.
    const vertexAlts = perVertexElevation ? coordinates.map(c => Number(c[2]) || 0) : null
    const hasVertexAlt = !!vertexAlts && vertexAlts.length === coordinates.length && vertexAlts.length > 1

    // 'msl' = altitude fixe (ligne plate à `fixedAltitude` ASL) ; sinon 'agl' (suit le sol).
    const isMsl = opts.mode === 'msl'
    const fixedAlt = Number(opts.fixedAltitude) || 0

    // Traces de vol : déjà très denses → on garde tel quel (densifier casserait l'index
    // line-progress). Sinon on densifie pour draper (agl) ; en msl la ligne est plate,
    // densifier est inutile mais inoffensif → on garde la géométrie densifiée pour le rideau.
    const renderCoords = hasVertexAlt ? coordinates : densifyPath(coordinates)

    map.addSource(sourceId, {
      type: 'geojson',
      lineMetrics: hasVertexAlt, // requis pour line-progress (z-offset par sommet)
      data: { type: 'Feature', properties: hasVertexAlt ? { elevation: vertexAlts } : {}, geometry: { type: 'LineString', coordinates: renderCoords } },
    })
    map.addLayer({
      id: layerId,
      type: 'line',
      source: sourceId,
      // line-elevation-reference + line-z-offset (LAYOUT) :
      //  - vol : z-offset par sommet (line-progress) au-dessus du sol ;
      //  - agl : z-offset constant au-dessus du sol (drape via sommets densifiés) ;
      //  - msl : référence 'sea' (= absolu) + z-offset = altitude → ligne plate à l'ASL.
      layout: {
        'line-elevation-reference': isMsl ? 'sea' : 'ground',
        'line-z-offset': hasVertexAlt
          ? ['at-interpolated', ['*', ['line-progress'], ['-', ['length', ['get', 'elevation']], 1]], ['get', 'elevation']]
          : (isMsl ? fixedAlt : (height || 0)),
      } as any,
      // line-occlusion-opacity : garde visible (semi-transparente) la portion de la ligne
      // élevée masquée par le relief en vue 3D inclinée. Par défaut à 0 → "une partie
      // disparaît" derrière les collines. Sans effet sur les lignes plaquées au sol.
      paint: { 'line-color': color || normalColor, 'line-width': 2, 'line-occlusion-opacity': 0.5 } as any,
    })

    // Rideau vertical optionnel (jupe sol → ligne).
    const curtainOpts = () => ({ mode: (isMsl ? 'msl' : 'agl') as 'agl' | 'msl', value: isMsl ? fixedAlt : height, color: currentColor })
    if (opts.curtain) setCurtain(id, coordinates.map(c => [c[0], c[1]]), curtainOpts())

    map.on('click', layerId, () => {
      store.commit('SET_SELECTED_MAP_ELEMENT', id)
    })
    map.on('mouseenter', layerId, () => { map.getCanvas().style.cursor = 'pointer' })
    map.on('mouseleave', layerId, () => { map.getCanvas().style.cursor = '' })

    coverMap[id] = [{
      type: 'polyline',
      remove: () => {
        removeCurtain(id)
        if (map.getLayer(layerId)) map.removeLayer(layerId)
        if (map.getSource(sourceId)) map.removeSource(sourceId)
      },
      setColor: (c: string) => {
        currentColor = c || normalColor
        if (map.getLayer(layerId)) map.setPaintProperty(layerId, 'line-color', c)
        setCurtainColor(id, currentColor)
      },
      setZOffset: (h: number) => {
        // Traces de vol : l'altitude est par sommet, on n'écrase pas avec une constante.
        if (hasVertexAlt) return
        if (map.getLayer(layerId)) map.setLayoutProperty(layerId, 'line-z-offset' as any, isMsl ? fixedAlt : (h || 0))
        if (opts.curtain) setCurtain(id, coordinates.map(c => [c[0], c[1]]), { ...curtainOpts(), value: isMsl ? fixedAlt : h })
      },
      setCoordinates: (coords: GeojsonCoordinate[]) => {
        ;(map.getSource(sourceId) as mapboxgl.GeoJSONSource)?.setData(
          { type: 'Feature', properties: hasVertexAlt ? { elevation: vertexAlts } : {}, geometry: { type: 'LineString', coordinates: hasVertexAlt ? coords : densifyPath(coords) } }
        )
        if (opts.curtain) setCurtain(id, coords.map(c => [c[0], c[1]]), curtainOpts())
      },
      getExtData: () => data,
    }]
  }

  function initPolygon (name: string, coordinates: GeojsonCoordinate[][], color?: string, data?: { id: string, [key: string]: any }, height = 0, opts: ElevationOpts = {}) {
    const map: mapboxgl.Map = root.$map
    if (!map) return
    const id = data?.id
    if (!id) return

    const sourceId = `polygon-${id}`
    const fillLayerId = `polygon-fill-${id}`
    const outlineLayerId = `polygon-outline-${id}`
    let currentColor = color || normalColor

    const isMsl = opts.mode === 'msl'
    const fixedAlt = Number(opts.fixedAltitude) || 0
    const ring = coordinates[0] || []
    const outerRing2d = () => ring.map(c => [c[0], c[1]])

    map.addSource(sourceId, {
      type: 'geojson',
      data: { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates } },
    })

    // ── Volume 3D conforme au relief (maillage custom, voir use-volume.ts). ──
    // fill-extrusion n'épouse pas le relief à l'intérieur d'un grand polygone (plateau) ;
    // on dessine donc un volume triangulé+subdivisé. Une couche `fill` plate au sol sert
    // d'empreinte ET de cible de clic (le maillage WebGL ne reçoit pas les events Mapbox).
    const floor = Number(opts.floor) || 0
    const ceiling = Number(opts.ceiling) || 0
    const isVolume = ceiling > floor || (opts.ceilingRef !== opts.floorRef && (ceiling > 0 || floor > 0))
    if (isVolume) {
      const rings2d = () => coordinates.map(r => r.map(c => [c[0], c[1]]))
      const volOpts = () => ({
        floor,
        ceiling,
        floorRef: (opts.floorRef === 'msl' ? 'msl' : 'agl') as 'agl' | 'msl',
        ceilingRef: (opts.ceilingRef === 'msl' ? 'msl' : 'agl') as 'agl' | 'msl',
        color: currentColor,
      })
      map.addLayer({
        id: fillLayerId,
        type: 'fill',
        source: sourceId,
        paint: { 'fill-color': color || normalColor, 'fill-opacity': 0.15 },
      })
      setVolume(id, rings2d(), volOpts())
      map.on('click', fillLayerId, () => { store.commit('SET_SELECTED_MAP_ELEMENT', id) })
      map.on('mouseenter', fillLayerId, () => { map.getCanvas().style.cursor = 'pointer' })
      map.on('mouseleave', fillLayerId, () => { map.getCanvas().style.cursor = '' })

      coverMap[id] = [{
        type: 'polygon',
        remove: () => {
          removeVolume(id)
          if (map.getLayer(fillLayerId)) map.removeLayer(fillLayerId)
          if (map.getSource(sourceId)) map.removeSource(sourceId)
        },
        setColor: (c: string) => {
          currentColor = c || normalColor
          if (map.getLayer(fillLayerId)) map.setPaintProperty(fillLayerId, 'fill-color', currentColor)
          setVolumeColor(id, currentColor)
        },
        setZOffset: () => { /* volume : plancher/plafond figés à la création, re-init sur changement */ },
        setCoordinates: (coords: GeojsonCoordinate[][]) => {
          ;(map.getSource(sourceId) as mapboxgl.GeoJSONSource)?.setData(
            { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: coords } }
          )
          setVolume(id, coords.map(r => r.map(c => [c[0], c[1]])), volOpts())
        },
        getExtData: () => data,
      }]
      return
    }

    // ── Surface simple : empreinte au sol + contour élevé (+ rideau optionnel). ──
    map.addLayer({
      id: fillLayerId,
      type: 'fill',
      source: sourceId,
      // Le remplissage reste au sol (empreinte) : `fill-elevation-reference` ne supporte
      // pas 'ground'/'sea'. L'élévation est portée par le contour + le rideau.
      paint: { 'fill-color': color || normalColor, 'fill-opacity': 0.4 },
    })
    map.addLayer({
      id: outlineLayerId,
      type: 'line',
      source: sourceId,
      // Contour élevé : 'ground' (suit le relief, +height) ou 'sea' (plat à l'altitude ASL).
      layout: {
        'line-elevation-reference': isMsl ? 'sea' : 'ground',
        'line-z-offset': isMsl ? fixedAlt : (height || 0),
      } as any,
      paint: { 'line-color': color || normalColor, 'line-width': 2 },
    })

    const curtainOpts = () => ({ mode: (isMsl ? 'msl' : 'agl') as 'agl' | 'msl', value: isMsl ? fixedAlt : height, color: currentColor })
    if (opts.curtain) setCurtain(id, outerRing2d(), curtainOpts())

    map.on('click', fillLayerId, () => {
      store.commit('SET_SELECTED_MAP_ELEMENT', id)
    })
    map.on('mouseenter', fillLayerId, () => { map.getCanvas().style.cursor = 'pointer' })
    map.on('mouseleave', fillLayerId, () => { map.getCanvas().style.cursor = '' })

    coverMap[id] = [{
      type: 'polygon',
      remove: () => {
        removeCurtain(id)
        if (map.getLayer(fillLayerId)) map.removeLayer(fillLayerId)
        if (map.getLayer(outlineLayerId)) map.removeLayer(outlineLayerId)
        if (map.getSource(sourceId)) map.removeSource(sourceId)
      },
      setColor: (c: string) => {
        currentColor = c || normalColor
        if (map.getLayer(fillLayerId)) map.setPaintProperty(fillLayerId, 'fill-color', c)
        if (map.getLayer(outlineLayerId)) map.setPaintProperty(outlineLayerId, 'line-color', c)
        setCurtainColor(id, currentColor)
      },
      setZOffset: (h: number) => {
        if (map.getLayer(outlineLayerId)) map.setLayoutProperty(outlineLayerId, 'line-z-offset' as any, isMsl ? fixedAlt : (h || 0))
        if (opts.curtain) setCurtain(id, outerRing2d(), { ...curtainOpts(), value: isMsl ? fixedAlt : h })
      },
      setCoordinates: (coords: GeojsonCoordinate[][]) => {
        ;(map.getSource(sourceId) as mapboxgl.GeoJSONSource)?.setData(
          { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: coords } }
        )
        if (opts.curtain) setCurtain(id, (coords[0] || []).map(c => [c[0], c[1]]), curtainOpts())
      },
      getExtData: () => data,
    }]
  }

  function initTextInfo (content: string, coordinates: GeojsonCoordinate, id: string) {
    const map: mapboxgl.Map = root.$map
    if (!map) return

    const el = document.createElement('div')
    el.textContent = content
    el.style.cssText = 'background:none;border:none;font-size:14px;color:white;text-shadow:1px 1px 2px black;pointer-events:none;white-space:nowrap;'

    const marker = new mapboxgl.Marker({ element: el, anchor: 'top' })
      .setLngLat([coordinates[0], coordinates[1]])
      .addTo(map)

    const existing = coverMap[id] || []
    existing.push({
      type: 'text',
      remove: () => marker.remove(),
      getText: () => el.textContent || '',
      setText: (text: string) => { el.textContent = text },
      setPosition: ([lng, lat]: [number, number]) => marker.setLngLat([lng, lat]),
      getExtData: () => ({ type: 'text', id }),
    })
    coverMap[id] = existing
  }

  function initCircle (name: string, center: GeojsonCoordinate, radius: number, data?: { id: string, [key: string]: any }, color?: string) {
    const map: mapboxgl.Map = root.$map
    if (!map) return
    const id = data?.id || name
    const fillColor = color || normalColor
    const coords = circleToPolygonCoords([center[0], center[1]], radius)
    const sourceId = `circle-${id}`
    const fillLayerId = `circle-fill-${id}`
    const outlineLayerId = `circle-outline-${id}`

    map.addSource(sourceId, {
      type: 'geojson',
      data: { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [coords] } },
    })
    map.addLayer({
      id: fillLayerId,
      type: 'fill',
      source: sourceId,
      paint: { 'fill-color': fillColor, 'fill-opacity': 0.2 },
    })
    map.addLayer({
      id: outlineLayerId,
      type: 'line',
      source: sourceId,
      paint: { 'line-color': fillColor, 'line-width': 2 },
    })

    coverMap[id] = [{
      type: 'circle',
      remove: () => {
        if (map.getLayer(fillLayerId)) map.removeLayer(fillLayerId)
        if (map.getLayer(outlineLayerId)) map.removeLayer(outlineLayerId)
        if (map.getSource(sourceId)) map.removeSource(sourceId)
      },
      setColor: (c: string) => {
        if (map.getLayer(fillLayerId)) map.setPaintProperty(fillLayerId, 'fill-color', c)
        if (map.getLayer(outlineLayerId)) map.setPaintProperty(outlineLayerId, 'line-color', c)
      },
      setCenterRadius: (newCenter: [number, number], newRadius: number) => {
        const newCoords = circleToPolygonCoords([newCenter[0], newCenter[1]], newRadius)
        ;(map.getSource(sourceId) as mapboxgl.GeoJSONSource)?.setData(
          { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [newCoords] } }
        )
        const textEntry = (coverMap[id] || []).find((e: any) => e.type === 'text')
        textEntry?.setPosition?.([newCenter[0], newCenter[1]])
      },
      getExtData: () => data,
    }]

    initTextInfo(name, center, id)
  }

  function initFlightAreaCircle (name: string, radius: number, position: GeojsonCoordinate, data: { id: string, type: EFlightAreaType, enable: boolean }) {
    const map: mapboxgl.Map = root.$map
    if (!map) return
    const { id, type, enable } = data
    const color = enable ? flightAreaColorMap[type] : disableColor
    const fillOpacity = EFlightAreaType.NFZ === type && enable ? 0.3 : 0

    const coords = circleToPolygonCoords([position[0], position[1]], radius)
    const sourceId = `flightcircle-${id}`
    const fillLayerId = `flightcircle-fill-${id}`
    const outlineLayerId = `flightcircle-outline-${id}`

    map.addSource(sourceId, {
      type: 'geojson',
      data: { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [coords] } },
    })
    map.addLayer({
      id: fillLayerId,
      type: 'fill',
      source: sourceId,
      paint: { 'fill-color': color, 'fill-opacity': fillOpacity },
    })
    map.addLayer({
      id: outlineLayerId,
      type: 'line',
      source: sourceId,
      paint: { 'line-color': color, 'line-width': 4, 'line-dasharray': EFlightAreaType.NFZ === type ? [5, 2] : [5, 1, 2] },
    })

    coverMap[id] = [{
      type: 'flightcircle',
      remove: () => {
        if (map.getLayer(fillLayerId)) map.removeLayer(fillLayerId)
        if (map.getLayer(outlineLayerId)) map.removeLayer(outlineLayerId)
        if (map.getSource(sourceId)) map.removeSource(sourceId)
      },
      update: (newRadius: number, newPos: [number, number], newColor: string, newFillOpacity: number) => {
        const newCoords = circleToPolygonCoords(newPos, newRadius)
        ;(map.getSource(sourceId) as mapboxgl.GeoJSONSource)?.setData(
          { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [newCoords] } }
        )
        if (map.getLayer(fillLayerId)) {
          map.setPaintProperty(fillLayerId, 'fill-color', newColor)
          map.setPaintProperty(fillLayerId, 'fill-opacity', newFillOpacity)
        }
        if (map.getLayer(outlineLayerId)) map.setPaintProperty(outlineLayerId, 'line-color', newColor)
      },
      getExtData: () => data,
    }]

    initTextInfo(name, position, id)
  }

  function updateFlightAreaCircle (id: string, name: string, radius: number, position: GeojsonCoordinate, enable: boolean, type: EFlightAreaType) {
    const elements = getElementFromMap(id)
    if (elements && elements.length > 0) {
      const textIndex = elements.findIndex((ele: any) => ele.getExtData()?.type === 'text')
      const shapeIndex = textIndex === -1 ? 0 : (textIndex ^ 1)
      const shape = elements[shapeIndex]
      const color = enable ? flightAreaColorMap[type] : disableColor
      const fillOpacity = EFlightAreaType.NFZ === type && enable ? 0.3 : 0
      shape?.update?.(radius, [position[0], position[1]], color, fillOpacity)

      if (textIndex === -1) {
        initTextInfo(name, position, id)
      } else {
        const text = elements[textIndex]
        text.setText(name)
        text.setPosition([position[0], position[1]])
      }
    } else {
      initFlightAreaCircle(name, radius, position, { id, type, enable })
    }
  }

  function calcPolygonPosition (coordinate: GeojsonCoordinate[]): GeojsonCoordinate {
    const index = coordinate.length - 1
    return [(coordinate[0][0] + coordinate[index][0]) / 2.0, (coordinate[0][1] + coordinate[index][1]) / 2]
  }

  function initFlightAreaPolygon (name: string, coordinates: GeojsonCoordinate[], data: { id: string, type: EFlightAreaType, enable: boolean }) {
    const map: mapboxgl.Map = root.$map
    if (!map) return
    const { id, type, enable } = data
    const color = enable ? flightAreaColorMap[type] : disableColor
    const fillOpacity = EFlightAreaType.NFZ === type && enable ? 0.3 : 0

    const sourceId = `flightpoly-${id}`
    const fillLayerId = `flightpoly-fill-${id}`
    const outlineLayerId = `flightpoly-outline-${id}`
    const coords = [coordinates]

    map.addSource(sourceId, {
      type: 'geojson',
      data: { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: coords } },
    })
    map.addLayer({
      id: fillLayerId,
      type: 'fill',
      source: sourceId,
      paint: { 'fill-color': color, 'fill-opacity': fillOpacity },
    })
    map.addLayer({
      id: outlineLayerId,
      type: 'line',
      source: sourceId,
      paint: { 'line-color': color, 'line-width': 4, 'line-dasharray': EFlightAreaType.NFZ === type ? [5, 2] : [5, 1, 2] },
    })

    coverMap[id] = [{
      type: 'flightpolygon',
      remove: () => {
        if (map.getLayer(fillLayerId)) map.removeLayer(fillLayerId)
        if (map.getLayer(outlineLayerId)) map.removeLayer(outlineLayerId)
        if (map.getSource(sourceId)) map.removeSource(sourceId)
      },
      update: (newCoords: GeojsonCoordinate[], newColor: string, newFillOpacity: number) => {
        ;(map.getSource(sourceId) as mapboxgl.GeoJSONSource)?.setData(
          { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [newCoords] } }
        )
        if (map.getLayer(fillLayerId)) {
          map.setPaintProperty(fillLayerId, 'fill-color', newColor)
          map.setPaintProperty(fillLayerId, 'fill-opacity', newFillOpacity)
        }
        if (map.getLayer(outlineLayerId)) map.setPaintProperty(outlineLayerId, 'line-color', newColor)
      },
      getExtData: () => data,
    }]

    initTextInfo(name, calcPolygonPosition(coordinates), id)
  }

  function updateFlightAreaPolygon (id: string, name: string, coordinates: GeojsonCoordinate[], enable: boolean, type: EFlightAreaType) {
    const elements = getElementFromMap(id)
    if (elements && elements.length > 0) {
      const textIndex = elements.findIndex((ele: any) => ele.getExtData()?.type === 'text')
      const shapeIndex = textIndex === -1 ? 0 : (textIndex ^ 1)
      const shape = elements[shapeIndex]
      const color = enable ? flightAreaColorMap[type] : disableColor
      const fillOpacity = EFlightAreaType.NFZ === type && enable ? 0.3 : 0
      shape?.update?.(coordinates, color, fillOpacity)

      if (textIndex === -1) {
        initTextInfo(name, calcPolygonPosition(coordinates), id)
      } else {
        const text = elements[textIndex]
        text.setText(name)
        text.setPosition(calcPolygonPosition(coordinates))
      }
    } else {
      initFlightAreaPolygon(name, coordinates, { id, type, enable })
    }
  }

  function removeCoverFromMap (id: string) {
    const items = coverMap[id]
    if (!items) return
    items.forEach((item: any) => item.remove?.())
    coverMap[id] = []
  }

  function getElementFromMap (id: string): any[] {
    return coverMap[id]
  }

  // NB : le mode d'élévation, l'altitude fixe et le rideau sont figés à la création
  // (références de couche + maillage). Quand `opts` est fourni (édition d'élévation),
  // on retire et on recrée pour appliquer le nouveau mode proprement.
  function updatePinElement (id: string, name: string, coordinates: GeojsonCoordinate, color?: string, height = 0, opts?: ElevationOpts) {
    const elements = getElementFromMap(id)
    if (!opts && elements && elements.length > 0) {
      const element = elements[0]
      element.setLngLat?.([coordinates[0], coordinates[1]])
      element.setIcon?.(getPinIconUrl(color))
    } else {
      removeCoverFromMap(id)
      init2DPin(name, coordinates, color, { id, name }, height, opts || {})
    }
  }

  function updatePolylineElement (id: string, name: string, coordinates: GeojsonCoordinate[], color?: string, height = 0, opts?: ElevationOpts) {
    const elements = getElementFromMap(id)
    if (!opts && elements && elements.length > 0) {
      elements[0].setColor?.(color || normalColor)
      elements[0].setZOffset?.(height)
    } else {
      removeCoverFromMap(id)
      initPolyline(name, coordinates, color, { id, name }, height, false, opts || {})
    }
  }

  function updatePolygonElement (id: string, name: string, coordinates: GeojsonCoordinate[][], color?: string, height = 0, opts?: ElevationOpts) {
    const elements = getElementFromMap(id)
    if (!opts && elements && elements.length > 0) {
      elements[0].setColor?.(color || normalColor)
      elements[0].setZOffset?.(height)
    } else {
      removeCoverFromMap(id)
      initPolygon(name, coordinates, color, { id, name }, height, opts || {})
    }
  }

  function updateCircleElement (id: string, name: string, center: GeojsonCoordinate, radius: number, color?: string) {
    const elements = getElementFromMap(id)
    if (elements && elements.length > 0 && elements[0].setColor && color) {
      // Layer already exists — just update the colour in-place (avoids remove/re-add flicker)
      elements[0].setColor(color)
    } else {
      removeCoverFromMap(id)
      initCircle(name, center, radius, { id, name }, color)
    }
  }

  return {
    init2DPin,
    initPolyline,
    initPolygon,
    initCircle,
    removeCoverFromMap,
    getElementFromMap,
    updatePinElement,
    updatePolylineElement,
    updatePolygonElement,
    updateCircleElement,
    initFlightAreaCircle,
    initFlightAreaPolygon,
    updateFlightAreaPolygon,
    updateFlightAreaCircle,
    calcPolygonPosition,
  }
}
